import { DeletedPaymentDetails, PaymentDetailsDiscrepancies } from '@approvalmax/types';
import { backend, domain } from 'modules/data';
import { Dispatch, PropsWithChildren, SetStateAction } from 'react';

export enum SortingDirection {
    Ascending = 'Ascending',
    Descending = 'Descending',
}

export interface FilterTrasferData
    extends Omit<backend.transfers.BillRequestsForAmaxPayBatchPaymentTransfer, 'companyId'> {}

export interface SelectedSimplifiedBankAccount {
    id: string;
    name: string;
    iconUrl?: string;
    number?: string | null;
    sortCode?: string | null;
    iban?: string | null;
    currencyCode?: string;
    connected?: boolean;
}

export interface XeroAmaxPayBatchPaymentFormContextType {
    billBatchPayment: domain.XeroAmaxPayBatchPayment;
    setBillBatchPayment: (
        newBillBatchPayment: domain.XeroAmaxPayBatchPayment,
        propName: keyof domain.XeroAmaxPayBatchPayment
    ) => void;
    showErrors: boolean;
    setShowErrors: (newValue: boolean) => void;
    saveAndSubmit: (commentText?: string, newDetails?: domain.XeroAmaxPayBatchPayment) => void;
    clearItems: () => void;
    addBills: (selectableBills: domain.BatchPaymentSelectableBillItem[]) => void;
    removeSelectableBillById: (xeroBillInvoiceRequestId: string) => void;
    changeBillInvoiceItemField: (
        xeroBillInvoiceRequestId: string,
        itemField: keyof domain.AmaxPayPaymentItem,
        newValue: domain.AmaxPayPaymentItem[keyof domain.AmaxPayPaymentItem]
    ) => void;
    changePaymentDetails: (xeroBillInvoiceRequestId: string, paymentDetails: domain.PaymentDetails) => void;
    billInvoiceRequestIdsWithError: string[];
    billInvoiceRequestIdsWithExceededAmount: string[];
    isSaving: boolean;
    companyId: string;
    companyCurrency: string;
    selectedSimplifiedBankAccount?: SelectedSimplifiedBankAccount;
    setSelectedSimplifiedBankAccount: Dispatch<SetStateAction<SelectedSimplifiedBankAccount | undefined>>;
    deletedPaymentDetails: DeletedPaymentDetails[];
    setDeletedPaymentDetails: Dispatch<SetStateAction<DeletedPaymentDetails[]>>;
    paymentDetailsDiscrepancies: PaymentDetailsDiscrepancies[];
    setPaymentDetailsDiscrepancies: Dispatch<SetStateAction<PaymentDetailsDiscrepancies[]>>;
    applyPaymentDetailsDiscrepancies: () => void;
    changeIsMergedBySupplier: Dispatch<SetStateAction<boolean>>;
    referencesForMergedItems: Record<string, string>;
    changeReferencesForMergedItems: Dispatch<SetStateAction<Record<string, string>>>;
}

export interface XeroAmaxPayBatchPaymentFormContextProviderProps extends PropsWithChildren {
    request: domain.XeroAmaxPayBatchPaymentRequest;
    editRequestField: <TDetails, TKey extends keyof TDetails = keyof TDetails>(
        propName: TKey,
        newValue: TDetails[TKey]
    ) => void;
    editRequestFieldImmediately: <TDetails, TKey extends keyof TDetails = keyof TDetails>(
        propName: TKey,
        newValue: TDetails[TKey]
    ) => void;
}

export interface UsePrepopulateSelectedBankAccountFromLinesParams {
    selectedSimplifiedBankAccount?: XeroAmaxPayBatchPaymentFormContextType['selectedSimplifiedBankAccount'];
    setSelectedSimplifiedBankAccount: Dispatch<
        SetStateAction<XeroAmaxPayBatchPaymentFormContextType['selectedSimplifiedBankAccount']>
    >;
    bankAccountFromBill?: XeroAmaxPayBatchPaymentFormContextType['selectedSimplifiedBankAccount'];
}

export interface UseSaveAndSubmitMutationParams {
    xeroAmaxPayBatchPayment: domain.XeroAmaxPayBatchPayment;
    request: domain.XeroAmaxPayBatchPaymentRequest;
    setBillInvoiceRequestIdsWithExceededAmount: (billInvoiceRequestIdsWithExceededAmount: string[]) => void;
    setBillInvoiceRequestIdsWithError: (billInvoiceRequestIdsWithError: string[]) => void;
    setDeletedPaymentDetails: (deletedPaymentDetails: DeletedPaymentDetails[]) => void;
    setPaymentDetailsDiscrepancies: (paymentDetailsDiscrepancies: PaymentDetailsDiscrepancies[]) => void;
}
